import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { useHolidays } from "../hooks";
import { useDeleteHolidays } from "../hooks/use-holiday-mutation";
import { useHolidayUIStore } from "../store/use-holiday-ui-store";
import { formatHolidayDateRange } from "../utils/format-holiday-date-range";

export function HolidayBulkDeleteDialog() {
  const { t } = useTranslation("settings");
  const { holidays } = useHolidays();
  const { isBulkDeleteDialogOpen, selectedHolidayIds, closeBulkDeleteDialog } = useHolidayUIStore();
  const selectedHolidays = holidays.filter((holiday) => selectedHolidayIds.includes(holiday.id));
  const { deleteHolidays, deleting } = useDeleteHolidays(holidays);

  const handleDelete = async () => {
    if (selectedHolidays.length > 0) {
      try {
        await deleteHolidays(selectedHolidays.map((holiday) => holiday.id));
      } catch {
        return;
      }
    }
  };

  return (
    <AlertDialog
      open={isBulkDeleteDialogOpen}
      onOpenChange={(open) => !open && !deleting && closeBulkDeleteDialog()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("holidays.bulk.dialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("holidays.bulk.dialog.description", { count: selectedHolidays.length })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-64 space-y-2 overflow-y-auto">
          {selectedHolidays.map((holiday) => (
            <li key={holiday.id} className="rounded-md border p-3">
              <div className="font-medium">{holiday.name}</div>
              <div className="text-sm text-muted-foreground">
                {formatHolidayDateRange(holiday.startDateTime, holiday.endDateTime)}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-sm font-medium text-destructive">
          {t("holidays.bulk.dialog.irreversible")}
        </p>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>{t("holidays.bulk.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void handleDelete();
            }}
            disabled={deleting || selectedHolidays.length === 0}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleting && <Spinner className="mr-2 h-4 w-4" />}
            {t("holidays.bulk.confirmDelete", { count: selectedHolidays.length })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
